import { useRouter } from 'next/router';
import { useReduxState } from 'hooks';
import { getOpeningHoursStatus } from 'shared-helpers/courses';
import { getAccessStatus } from 'lib/courses';
import { LinkButton } from 'components/common';
import { CoursesUserActionJoin, CoursesUserActionRequestAccess } from '.';

import type { CourseInView } from './Courses';

interface Props {
  course: CourseInView;
}

const CoursesUserAction = ({ course }: Props) => {
  const {
    messages,
    serverConfig: { timeZone },
    user
  } = useReduxState();
  const router = useRouter();

  const { canBeJoined, canBeOpened, canBeRequested } = getAccessStatus({
    course,
    user
  });

  const openingHoursStatus = getOpeningHoursStatus({
    openingHours: course.openingHours,
    timeZone
  });

  const courseIsClosed =
    course.openingHours?.isEnabled && !openingHoursStatus?.isCurrentlyOpen;

  const {
    chapters,
    isAddedUser,
    isAdmin,
    isGroupUser,
    isOwnerOrSuperadmin,
    isPathUser,
    slug,
    unreadChatCount
  } = course;

  const inCourse = isAddedUser || isGroupUser || isPathUser;

  const languageVersion =
    typeof router.query.languageVersion === 'string'
      ? router.query.languageVersion
      : null;

  const href = {
    pathname: '/courses/[...courseParams]',
    query: {
      courseParams: [slug] as string[]
    } as { courseParams: string[]; languageVersion?: string }
  };
  if (chapters.length) href.query.courseParams.push(chapters[0].slug);
  if (languageVersion) href.query.languageVersion = languageVersion;

  let courseHref = `/courses/${slug}${
    chapters.length ? `/${chapters[0].slug}` : ''
  }`;
  if (languageVersion) {
    courseHref += `?languageVersion=${encodeURIComponent(languageVersion)}`;
  }

  return (
    <div className="actions">
      {canBeOpened || user?.roles.includes('guest') ? (
        <LinkButton
          disabled={courseIsClosed}
          href={courseHref}
          isRounded={true}
          onClick={(ev) => ev.stopPropagation()}
          size="small"
          style={{ fontSize: '1.3rem', padding: '6px 12px 7px' }}
          text={messages.commonOpen}
          tooltip={
            unreadChatCount && (isOwnerOrSuperadmin || isAdmin || inCourse)
              ? `${unreadChatCount} ${messages.coursesUnreadChatMessages}`
              : courseIsClosed
              ? messages.courseIsClosed
              : ''
          }
          unreadItems={
            isOwnerOrSuperadmin || isAdmin || inCourse ? unreadChatCount : null
          }
        />
      ) : canBeJoined ? (
        <CoursesUserActionJoin course={course} />
      ) : canBeRequested ? (
        <CoursesUserActionRequestAccess course={course} />
      ) : null}

      <style jsx>{`
        .actions {
          display: flex;
        }
        .content-links {
          display: flex;
          flex-grow: 1;
          flex-shrink: 0;
        }
        .details {
          margin-right: 10px;
        }
      `}</style>
    </div>
  );
};

export default CoursesUserAction;
